/**
 * Worst-case analysis (PRD §4.5).
 *
 * The naive implementation — evaluate the 2^n corners, report min and max —
 * is wrong in two distinct ways, and this module addresses both.
 *
 * 1. It is exponential. Sign analysis of the partial derivatives collapses it
 *    to two evaluations when the function is monotonic in each variable.
 *
 * 2. For a non-monotonic function the extreme need not be at a corner at all.
 *    Enumerating every corner still misses an interior peak. So monotonicity is
 *    *tested*, and when it fails the tool reports a guaranteed outer bound from
 *    interval arithmetic alongside the best achievable inner estimate, and says
 *    plainly that the truth lies between them.
 *
 * Reporting an inner estimate as though it were an absolute limit is the worst
 * failure mode available to a tolerance analyser: it understates the risk, and
 * it does so silently.
 */

import type { CompiledModel } from './model.ts';
import { variableBounds } from './model.ts';
import { compile, type Compiled } from './expr/compile.ts';
import { differentiate, NonDifferentiable } from './expr/differentiate.ts';
import { evalInterval, iv, type Interval } from './intervals.ts';
import { DEFAULT_SIGMA_CLAMP, nominal } from './distributions.ts';
import { CounterRng } from './prng.ts';

export interface Contribution {
  varId: string;
  /** Partial derivative at the nominal point. */
  partial: number;
  /** Half the variable's box width. */
  halfRange: number;
  /** |partial| * halfRange — this variable's share of the worst-case swing. */
  contribution: number;
  /** Share of the summed contributions, in [0,1]. */
  fraction: number;
  /** Which end of its range drives the output maximum. */
  cornerForMax: 'min' | 'max';
  clamped: boolean;
}

export type WcaMethod = 'monotonic-corner' | 'enumerated+interval' | 'search+interval';

export interface WcaResult {
  outputId: string;
  nominal: number;
  /** The headline worst case. Conservative (outer) whenever monotonicity fails. */
  min: number;
  max: number;
  method: WcaMethod;
  monotonic: boolean;
  /** Variables whose partial derivative changes sign inside the box. */
  nonMonotonicVars: string[];
  /** Achievable extremes found by evaluation — a lower bound on the true range. */
  innerMin: number;
  innerMax: number;
  /** Guaranteed containment from interval arithmetic — an upper bound on the true range. */
  outerMin: number;
  outerMax: number;
  contributions: Contribution[];
  /** Sigma clamp applied to unbounded distributions, and who it affected. */
  sigmaClamp: number;
  clampedVars: string[];
  /** Derivatives came from finite differences rather than symbolic form. */
  numericDerivatives: boolean;
  /** Findings that mean something about the model and deserve a callout. */
  warnings: string[];
  /**
   * Facts about how this result was produced, not problems with the model.
   *
   * Kept apart from `warnings` because they compete for the same attention and
   * are not the same kind of thing: the +/-3 sigma clamp is unavoidable for any
   * unbounded distribution and fires on every well-formed model that uses one,
   * so raising it to the same level as "your transfer function is non-monotonic"
   * teaches users that warnings are wallpaper. Belongs beside the chart it
   * explains.
   */
  notes: string[];
}

export interface WcaOptions {
  sigmaClamp?: number;
  /** Points used to test each partial's sign across the box. */
  monotonicityProbes?: number;
  /** Corner count above which enumeration is skipped in favour of random search. */
  maxEnumerationVars?: number;
  searchPoints?: number;
  seed?: number;
}

/** Compile d(output)/d(var) for every variable, or fall back to finite differences. */
function buildPartials(
  model: CompiledModel,
  outputIndex: number,
): { fns: (Compiled | null)[]; numeric: boolean } {
  const out = model.outputs[outputIndex];
  const fns: (Compiled | null)[] = [];
  let numeric = false;

  for (const varId of model.varIds) {
    try {
      fns.push(compile(differentiate(out.inlined, varId), model.varIndex));
    } catch (e) {
      if (e instanceof NonDifferentiable) {
        fns.push(null);
        numeric = true;
      } else {
        throw e;
      }
    }
  }
  return { fns, numeric };
}

export function analyzeWorstCase(
  model: CompiledModel,
  outputIndex: number,
  options: WcaOptions = {},
): WcaResult {
  const sigmaClamp = options.sigmaClamp ?? DEFAULT_SIGMA_CLAMP;
  const probes = options.monotonicityProbes ?? 1000;
  const maxEnumVars = options.maxEnumerationVars ?? 16;
  const searchPoints = options.searchPoints ?? 8192;
  const seed = options.seed ?? model.project.seed;

  const out = model.outputs[outputIndex];
  const n = model.varIds.length;
  const { min: boxMin, max: boxMax, clamped } = variableBounds(model, sigmaClamp);
  const warnings: string[] = [];
  const notes: string[] = [];

  const evalAt = compile(out.inlined, model.varIndex);
  const point = new Float64Array(n);

  const nominalPoint = new Float64Array(n);
  for (let i = 0; i < n; i++) nominalPoint[i] = nominal(model.distributions[i]);
  const nominalValue = evalAt(nominalPoint);

  const { fns: partialFns, numeric } = buildPartials(model, outputIndex);

  const partialAt = (env: Float64Array, i: number): number => {
    const fn = partialFns[i];
    if (fn) return fn(env);
    // Central difference, scaled to the variable's own range so the step is
    // meaningful whether the quantity is 1e-12 farads or 1e5 ohms.
    const span = boxMax[i] - boxMin[i];
    const h = span > 0 ? span * 1e-6 : Math.max(Math.abs(env[i]), 1) * 1e-6;
    const saved = env[i];
    env[i] = saved + h;
    const up = evalAt(env);
    env[i] = saved - h;
    const down = evalAt(env);
    env[i] = saved;
    return (up - down) / (2 * h);
  };

  // --- Monotonicity: does any partial change sign inside the box? ------------
  const rng = new CounterRng(seed ^ 0x5ca1ab1e);
  const signSeen = Array.from({ length: n }, () => ({ pos: false, neg: false }));

  const recordSigns = (env: Float64Array) => {
    for (let i = 0; i < n; i++) {
      if (boxMax[i] === boxMin[i]) continue;
      const d = partialAt(env, i);
      // A negligible derivative is not evidence of a sign change.
      if (d > 1e-12) signSeen[i].pos = true;
      else if (d < -1e-12) signSeen[i].neg = true;
    }
  };

  recordSigns(nominalPoint);
  for (let p = 0; p < probes; p++) {
    for (let i = 0; i < n; i++) {
      point[i] = boxMin[i] + rng.uniform(p, i) * (boxMax[i] - boxMin[i]);
    }
    recordSigns(point);
  }

  const nonMonotonicVars: string[] = [];
  for (let i = 0; i < n; i++) {
    if (signSeen[i].pos && signSeen[i].neg) nonMonotonicVars.push(model.varIds[i]);
  }
  const monotonic = nonMonotonicVars.length === 0;

  // --- Sign-directed corner --------------------------------------------------
  const nominalPartials = new Float64Array(n);
  for (let i = 0; i < n; i++) nominalPartials[i] = partialAt(nominalPoint, i);

  const cornerFor = (wantMax: boolean): Float64Array => {
    const p = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const positive = nominalPartials[i] >= 0;
      const takeMax = wantMax ? positive : !positive;
      p[i] = takeMax ? boxMax[i] : boxMin[i];
    }
    return p;
  };

  let innerMin = evalAt(cornerFor(false));
  let innerMax = evalAt(cornerFor(true));
  const consider = (v: number) => {
    if (Number.isFinite(v)) {
      if (v < innerMin) innerMin = v;
      if (v > innerMax) innerMax = v;
    }
  };
  consider(nominalValue);

  let method: WcaMethod = 'monotonic-corner';

  if (!monotonic) {
    if (n <= maxEnumVars) {
      method = 'enumerated+interval';
      const total = 1 << n;
      for (let mask = 0; mask < total; mask++) {
        for (let i = 0; i < n; i++) point[i] = mask & (1 << i) ? boxMax[i] : boxMin[i];
        consider(evalAt(point));
      }
    } else {
      method = 'search+interval';
      for (let s = 0; s < searchPoints; s++) {
        for (let i = 0; i < n; i++) {
          const u = rng.uniform(0x40000000 + s, i);
          // Half the probes at corners, half in the interior: corners find
          // extremes of monotone-ish regions, interior points find peaks.
          point[i] = s % 2 === 0
            ? u < 0.5 ? boxMin[i] : boxMax[i]
            : boxMin[i] + u * (boxMax[i] - boxMin[i]);
        }
        consider(evalAt(point));
      }
    }
  }

  // --- Guaranteed outer bound ------------------------------------------------
  const box = new Map<string, Interval>();
  for (let i = 0; i < n; i++) box.set(model.varIds[i], iv(boxMin[i], boxMax[i]));

  let outer: Interval;
  try {
    outer = evalInterval(out.inlined, box);
  } catch {
    outer = iv(-Infinity, Infinity);
  }
  // Interval arithmetic over-approximates but can never be tighter than truth,
  // so an inner estimate outside it signals a bug rather than a better bound.
  const outerMin = Math.min(outer.lo, innerMin);
  const outerMax = Math.max(outer.hi, innerMax);

  // --- Contributor table -----------------------------------------------------
  const contributions: Contribution[] = model.varIds.map((varId, i) => {
    const halfRange = (boxMax[i] - boxMin[i]) / 2;
    return {
      varId,
      partial: nominalPartials[i],
      halfRange,
      contribution: Math.abs(nominalPartials[i]) * halfRange,
      fraction: 0,
      cornerForMax: nominalPartials[i] >= 0 ? 'max' : 'min',
      clamped: clamped[i],
    };
  });
  const totalContribution = contributions.reduce((a, c) => a + c.contribution, 0);
  if (totalContribution > 0) {
    for (const c of contributions) c.fraction = c.contribution / totalContribution;
  }
  contributions.sort((a, b) => b.contribution - a.contribution);

  // --- Warnings --------------------------------------------------------------
  if (!monotonic) {
    warnings.push(
      `Output is not monotonic in ${nonMonotonicVars.join(', ')}. Corner evaluation ` +
        `cannot be trusted to find the true extremes, so the reported limits are the ` +
        `guaranteed interval-arithmetic bound. The achievable range found by ` +
        `${method === 'enumerated+interval' ? 'corner enumeration' : 'search'} is ` +
        `[${innerMin.toPrecision(6)}, ${innerMax.toPrecision(6)}]; the true worst case ` +
        `lies between the two.`,
    );
  }
  if (numeric) {
    warnings.push(
      'Expression contains abs/min/max/if/rounding, so sensitivities come from finite ' +
        'differences and are undefined at the switching points.',
    );
  }
  const clampedVars = model.varIds.filter((_, i) => clamped[i]);
  if (clampedVars.length > 0) {
    notes.push(
      `Unbounded distributions were clamped at +/-${sigmaClamp} sigma for ${clampedVars.join(', ')}. ` +
        'These limits are therefore not absolute — they are the worst case within the stated clamp.',
    );
  }
  if (!Number.isFinite(outerMin) || !Number.isFinite(outerMax)) {
    warnings.push(
      'Interval bound is unbounded — the expression may divide by a range spanning zero.',
    );
  }

  const useOuter = !monotonic;

  return {
    outputId: out.spec.id,
    nominal: nominalValue,
    min: useOuter ? outerMin : innerMin,
    max: useOuter ? outerMax : innerMax,
    method,
    monotonic,
    nonMonotonicVars,
    innerMin,
    innerMax,
    outerMin,
    outerMax,
    contributions,
    sigmaClamp,
    clampedVars,
    numericDerivatives: numeric,
    warnings,
    notes,
  };
}
